import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { MessageCircle, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';

type WaStatus = {
  status?: string;
  qr?: string | null;
  info?: { pushname?: string; wid?: string } | null;
};

const STATUS_LABEL: Record<string, string> = {
  idle: 'ממתין',
  initializing: 'מתחבר…',
  qr: 'ממתין לסריקה',
  connected: 'מחובר',
  disconnected: 'מנותק',
};

export default function WhatsAppConnectPage() {
  const [state, setState] = useState<WaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/platform/whatsapp/status');
      setState(r.data);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'שירות WhatsApp לא זמין');
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
  }, [load]);

  async function start() {
    setStarting(true);
    try {
      await api.post('/platform/whatsapp/start');
      toast.success('מפעיל חיבור…');
      void load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'כשל בהפעלה');
    } finally {
      setStarting(false);
    }
  }

  const status = state?.status || 'idle';
  const label = STATUS_LABEL[status] || status;

  return (
    <div className="space-y-8 page-enter">
      <div>
        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-sage-600 mb-2">
          Platform
        </p>
        <h1 className="font-sans text-4xl font-bold text-ink tracking-tight">חיבור WhatsApp</h1>
        <p className="text-ink-mute mt-1.5 text-[15px]">
          מספר ייעודי אחד לכל המערכת — סרקו QR ממכשירים מקושרים בוואטסאפ
        </p>
      </div>

      <div className="surface p-6 sm:p-8 max-w-lg space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="text-sage-600" size={20} />
            <span className="font-semibold text-ink">סטטוס</span>
          </div>
          <span
            className={`text-[12px] font-medium px-2.5 py-1 rounded-full ${
              status === 'connected'
                ? 'bg-sage-50 text-sage-700'
                : status === 'qr'
                  ? 'bg-brass-50 text-brass-700'
                  : 'bg-paper-mist text-ink-mute'
            }`}
          >
            {loading ? 'טוען…' : label}
          </span>
        </div>

        {state?.info?.pushname && (
          <p className="text-sm text-ink-mute">מחובר כ־{state.info.pushname}</p>
        )}

        {status === 'qr' && state?.qr && (
          <div className="flex flex-col items-center gap-3 py-2">
            <img src={state.qr} alt="QR לחיבור WhatsApp" className="w-56 h-56 rounded-xl border border-line bg-white" />
            <p className="text-[13px] text-ink-mute text-center max-w-xs">
              וואטסאפ → הגדרות → מכשירים מקושרים → קישור מכשיר
            </p>
          </div>
        )}

        {status === 'connected' && (
          <p className="text-sm text-sage-700">
            החיבור פעיל. הזמנות יוצאות דרך המספר המחובר לכל החתונות במערכת.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={start} disabled={starting} className="btn-primary">
            {starting ? 'מפעיל…' : status === 'connected' ? 'רענון חיבור' : 'התחל חיבור'}
          </button>
          <button type="button" onClick={() => void load()} className="btn-secondary">
            <RefreshCw size={15} />
            רענון סטטוס
          </button>
        </div>
      </div>
    </div>
  );
}
